<template>
  <div class="wg-layout">
    <div class="wg-layout__header">
      <div class="wg-layout__back-row">
        <router-link to="/sites" class="wg-layout__back-link">
          <i class="material-icons">arrow_back</i>
          <span>To All Sites</span>
        </router-link>
        <white-glove-admin-mode-toggle />
      </div>

      <div class="wg-layout__title-row">
        <h1 class="wg-layout__site-name">
          {{ site.name }}
          <span
            v-if="site && site.name"
            class="wg-status-dot wg-layout__site-status"
            :style="wgSiteStatusDotStyle"
            :title="wgSiteStatusInsight.statusLabel"
            role="img"
            :aria-label="wgSiteStatusInsight.statusLabel ? `Status: ${wgSiteStatusInsight.statusLabel}` : 'Site status'"
          ></span>
        </h1>
      </div>

      <nav class="wg-layout__tabs">
        <router-link
          v-for="tab in tabs"
          :key="tab.name"
          :to="{ name: tab.name, params: { siteId: $route.params.siteId } }"
          class="wg-layout__tab"
          :class="{ 'wg-layout__tab--active': isTabActive(tab.name) }"
        >
          {{ tab.label }}
        </router-link>
      </nav>
    </div>

    <router-view />

    <async-tasks-popup
      v-if="siteId"
      :site-id="siteId"
      :campaign-id="domainLevelCampaign ? domainLevelCampaign.id : 0"
    />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import { mixin } from '@/components/mixin'
import {
  getWhiteGloveSiteCardInsight,
  parseWhiteGloveOrdersResponse,
  whiteGloveStatusDotStyle,
} from '@/utils/whiteGloveSiteList'
import { whiteGlovePlans } from '@/components/plans_constants'
import { getPureServicePkgName } from '@/utils'
import WhiteGloveAdminModeToggle from '@/components/SitesSingleView/WhiteGlove/WhiteGloveAdminModeToggle.vue'

export default {
  components: { AsyncTasksPopup, WhiteGloveAdminModeToggle },
  mixins: [mixin],
  provide () {
    return {
      refreshWhiteGloveOrders: () => this.loadWhiteGloveOrders(),
    }
  },
  data () {
    return {
      ordersLoading: true,
      whiteGloveOrders: [],
      whiteGloveSiteProjectStatuses: {},
      campaigns: [],
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    domainLevelCampaigns () {
      return this.campaigns.filter((item) => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      return this.domainLevelCampaigns.length > 0 ? this.domainLevelCampaigns[0] : null
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    tabs () {
      const t = [
        { label: 'Overview', name: 'OverviewWhiteGlove' },
        { label: 'Content plans', name: 'ContentPlansWhiteGlove' },
        { label: 'All content', name: 'AllContentWhiteGlove' },
        { label: 'Wins', name: 'WinsWhiteGlove' },
        { label: 'Order history', name: 'OrderHistoryWhiteGlove' },
        { label: 'Settings', name: 'SettingsWhiteGlove' },
      ]
      if (this.whiteGloveMissionControlAdminUi) {
        t.push({ label: 'SEO Tools', name: 'SeoToolsWhiteGlove' })
      }
      return t
    },
    isWhiteGloveUser () {
      const u = this.$store.state.user
      if (!u) return false
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : u.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    wgSiteStatusInsight () {
      const id = this.site && this.site.id
      if (!id) {
        return { dotColor: '#9e9e9e', statusLabel: '' }
      }
      return getWhiteGloveSiteCardInsight(id, this.whiteGloveOrders, this.whiteGloveSiteProjectStatuses)
    },
    wgSiteStatusDotStyle () {
      return whiteGloveStatusDotStyle(this.wgSiteStatusInsight.dotColor)
    },
    siteWhiteGloveOrders () {
      const id = Number(this.$route.params.siteId)
      return this.whiteGloveOrders.filter((o) => o.site_id === id)
    },
    /** Stable id for watch; omitting subAccountRelationId on /api/sites/ is valid (owner's sites). */
    whiteGloveSubAccountRelationId () {
      const r = this.$store.state.selectedSubAccountRelation
      if (r && typeof r === 'object' && r.id != null) return r.id
      return null
    },
  },
  watch: {
    whiteGloveSubAccountRelationId: {
      handler (newId, oldId) {
        if (newId === oldId) return
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
          this.ensureSeoToolsWhiteGloveRouteAllowed()
        })
      },
      immediate: true,
    },
    '$route.params.siteId': {
      handler () {
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
        })
      },
      immediate: false,
    },
    isWhiteGloveUser (v) {
      if (v) {
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
        })
      } else {
        this.whiteGloveOrders = []
        this.whiteGloveSiteProjectStatuses = {}
        this.campaigns = []
      }
    },
    '$route.name' () {
      this.ensureSeoToolsWhiteGloveRouteAllowed()
    },
    isWhiteGloveAdminMode () {
      this.ensureSeoToolsWhiteGloveRouteAllowed()
    },
    isMissionControlSession () {
      this.ensureSeoToolsWhiteGloveRouteAllowed()
    },
  },
  methods: {
    ensureSitesLoaded () {
      const siteId = parseInt(this.$route.params.siteId, 10)
      const hasSite = Number.isFinite(siteId) && this.$store.getters.siteById(siteId)
      if (this.$store.state.sites.length && hasSite) {
        return Promise.resolve()
      }
      return this.$store
        .dispatch('loadSites')
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.$store.commit('setSites', response.data.sites)
          }
        })
        .then(() => {
          if (!Number.isFinite(siteId) || this.$store.getters.siteById(siteId)) {
            return
          }
          return this.$store.dispatch('loadSite', siteId).then((res) => {
            if (res.data && res.data.status === 'SUCCESS' && res.data.site) {
              this.$store.commit('setSite', res.data.site)
            }
          })
        })
    },
    ensureSeoToolsWhiteGloveRouteAllowed () {
      if (this.$route.name !== 'SeoToolsWhiteGlove') return
      if (this.whiteGloveMissionControlAdminUi) return
      const siteId = this.$route.params.siteId
      if (siteId == null || siteId === '') return
      this.$router.replace({ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } })
    },
    isTabActive (tabName) {
      if (this.$route.name === tabName) return true
      if (tabName === 'ContentPlansWhiteGlove' && this.$route.name === 'ContentPlanDetailWhiteGlove') {
        return true
      }
      if (tabName === 'AllContentWhiteGlove' && this.$route.name === 'ContentOnlyDetailWhiteGlove') {
        return true
      }
      return false
    },
    loadCampaigns () {
      if (!this.siteId) {
        this.campaigns = []
        return Promise.resolve()
      }
      return this.$store.dispatch('getCampaignsForSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.campaigns = response.data.campaigns || []
          }
        })
    },
    loadWhiteGloveOrders () {
      if (!this.isWhiteGloveUser) {
        this.whiteGloveOrders = []
        this.whiteGloveSiteProjectStatuses = {}
        this.ordersLoading = false
        return Promise.resolve()
      }
      this.ordersLoading = true
      return this.$store
        .dispatch('getWhiteGloveOrders')
        .then((response) => {
          const parsed = parseWhiteGloveOrdersResponse(response)
          this.whiteGloveOrders = parsed.orders
          this.whiteGloveSiteProjectStatuses = parsed.siteProjectStatuses
        })
        .catch(() => {
          this.whiteGloveOrders = []
          this.whiteGloveSiteProjectStatuses = {}
        })
        .finally(() => {
          this.ordersLoading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-layout {
  padding: 20px 20px 0;

  &__header {
    margin-bottom: 20px;
  }

  &__back-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--v-mainColor-base);
    font-size: 0.9rem;
    font-weight: 500;
    text-decoration: none;

    .material-icons {
      font-size: 18px;
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  &__site-name {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
    margin: 0;
  }

  &__site-status {
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
  }

  &__actions {
    display: flex;
    gap: 12px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 24px;
    border-radius: 20px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    box-sizing: border-box;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }

    &--outline {
      background: transparent;
      border: 2px solid var(--v-profileAnchorColor-base);
      color: var(--v-profileAnchorColor-base);
    }

    &--filled {
      background: var(--v-profileAnchorColor-base);
      border: 2px solid var(--v-profileAnchorColor-base);
      color: #fff;
    }
  }

  &__tabs {
    display: flex;
    gap: 10px;
    border-bottom: 1px solid var(--v-grayColor-base);
  }

  &__tab {
    padding: 8px 10px;
    font-size: 1rem;
    color: #2C2C2C;
    text-decoration: none;
    border-bottom: 3px solid transparent;
    transition: color 0.15s, border-color 0.15s;

    &--active {
      border-bottom-color: var(--v-profileAnchorColor-base);
      font-weight: 700;
    }
  }
}
</style>
